/**
 * ========================
 * HR / MEMBERS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the HR Management domain (mounted at `/app/members/*`
 * for backwards-compat with the existing route mount). Sections:
 *
 *  1. My HR              — every authenticated user (Dashboard / Calendar / Announcements)
 *  2. My Records         — every authenticated user (Profile, Leaves, Duties, Policies, Files)
 *  3. Quick Actions      — every authenticated user (Apply for Leave, Sign Policies, etc.)
 *  4. HR Operations      — HRAdmin only (employees, leaves, policies)
 *  5. HR Administration  — HRAdmin only (settings + lookups + PaySpace)
 *
 * Security note: the corresponding API endpoints enforce the same role rules
 * (HRAdmin for /api/* admin, Staff for /api/staff-portal/*). The menu visibility
 * is for UX only — the backend is the source of truth.
 */

import {
  IconUserCircle,
  IconCalendarTime,
  IconBriefcase,
  IconShieldCheck,
  IconLayoutDashboard,
  IconUsers,
  IconClipboardList,
  IconBookmarks,
  IconDatabase,
  IconSettings,
  IconBuildingBank,
  IconHome,
  IconCalendar,
  IconFolder,
  IconPlus,
  IconBell,
  IconChecklist,
  IconCircleCheck,
  IconHierarchy,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck, anyVisibilityCheck } from './BaseMenuProvider';
import { BACKEND_ROLES } from '@/config/permissions';
import { useHrBadgesStore } from '@/domain/badges/hrBadgesStore';
import { useLeaveApprovalBadgesStore, canSeeLeaveApprovalsMenu } from '@/domain/badges/leaveApprovalBadgesStore';

class MembersMenuProvider implements DomainMenuProvider {
  domainId = 'members';

  getMenuConfig(): DomainMenuConfig {
    const allUsers = createVisibilityCheck();
    const hrAdmin = anyVisibilityCheck({ roles: [BACKEND_ROLES.HR_ADMIN] });

    const sections: MenuSection[] = [
      // Overview pages every staff member lands on first.
      {
        id: 'hr-my',
        label: 'My HR',
        items: [
          { id: 'hr-my-dashboard',     label: 'Dashboard',     path: '/app/members/dashboard',                  icon: <IconHome size={16} />,         visible: allUsers },
          { id: 'hr-my-calendar',      label: 'Calendar',      path: '/app/members/calendar',                   icon: <IconCalendar size={16} />,     visible: allUsers },
          { id: 'hr-my-announcements', label: 'Announcements', path: '/app/members/announcements',              icon: <IconBell size={16} />,         visible: allUsers },
          // Line approvers only. Visibility is data-driven (not a role): the
          // capabilities baseline + live deltas in useLeaveApprovalBadgesStore.
          { id: 'hr-leave-approvals',  label: 'Leave Approvals', path: '/app/members/approvals',                icon: <IconChecklist size={16} />,    visible: () => canSeeLeaveApprovalsMenu(),
            countSelector: () => useLeaveApprovalBadgesStore.getState().myPendingApprovals || null, badgeColor: 'orange' },
        ],
      },

      // Personal records — the day-to-day surface area of each user's HR data.
      {
        id: 'hr-my-records',
        label: 'My Records',
        collapsible: true,
        defaultOpen: true,
        items: [
          { id: 'hr-my-profile',  label: 'My Profile',  path: '/app/members/my-profile',  icon: <IconUserCircle size={16} />,   visible: allUsers },
          { id: 'hr-my-leaves',   label: 'My Leaves',   path: '/app/members/my-leaves',   icon: <IconCalendarTime size={16} />, visible: allUsers,
            countSelector: () => useHrBadgesStore.getState().myLeavesActionNeeded || null, badgeColor: 'orange' },
          { id: 'hr-my-duties',   label: 'My Duties',   path: '/app/members/my-duties',   icon: <IconBriefcase size={16} />,    visible: allUsers },
          { id: 'hr-my-policies', label: 'My Policies', path: '/app/members/my-policies', icon: <IconShieldCheck size={16} />,  visible: allUsers },
          { id: 'hr-my-files',    label: 'My Files',    path: '/app/members/my-files',    icon: <IconFolder size={16} />,       visible: allUsers },
        ],
      },

      // High-frequency shortcuts. Pure-navigation links removed in favour of
      // genuine action-oriented entries — anything that just navigates to a
      // page already in My Records is redundant.
      {
        id: 'hr-my-actions',
        label: 'Quick Actions',
        collapsible: true,
        defaultOpen: true,
        items: [
          { id: 'hr-quick-apply-leave', label: 'Apply for Leave', path: '/app/members/my-leaves/new', icon: <IconPlus size={16} />, visible: allUsers, badgeColor: 'blue' },
        ],
      },
      {
        id: 'hr-ops',
        label: 'HR Operations',
        visible: hrAdmin,
        items: [
          { id: 'hr-dashboard',          label: 'Overview',            path: '/app/members/admin/dashboard',          icon: <IconLayoutDashboard size={16} />, visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN],
            countSelector: () => useHrBadgesStore.getState().onLeaveToday      || null, badgeColor: 'blue' },
          { id: 'hr-employees',          label: 'Employees',           path: '/app/members/admin/employees',          icon: <IconUsers size={16} />,           visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN],
            countSelector: () => useHrBadgesStore.getState().contractsExpiring || null, badgeColor: 'yellow' },
          { id: 'hr-leaves',             label: 'Leave Requests',      path: '/app/members/admin/leaves',             icon: <IconClipboardList size={16} />,   visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN],
            countSelector: () => useHrBadgesStore.getState().pendingLeaves     || null, badgeColor: 'orange' },
          { id: 'hr-policies',           label: 'Policies',            path: '/app/members/admin/policies',           icon: <IconBookmarks size={16} />,       visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN],
            countSelector: () => useHrBadgesStore.getState().policiesAwaiting  || null, badgeColor: 'red' },
          { id: 'hr-announcements',      label: 'Announcements',       path: '/app/members/admin/announcements',      icon: <IconBell size={16} />,            visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN] },
        ],
      },
      {
        id: 'hr-admin',
        label: 'HR Administration',
        visible: hrAdmin,
        items: [
          { id: 'hr-final-approvals',       label: 'Final Approvals',          path: '/app/members/admin/final-approvals',         icon: <IconCircleCheck size={16} />, visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN],
            countSelector: () => useLeaveApprovalBadgesStore.getState().finalQueue || null, badgeColor: 'red' },
          { id: 'hr-leave-approval-rules',  label: 'Approval Rules',           path: '/app/members/admin/leave-approval-rules',    icon: <IconHierarchy size={16} />,   visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN] },
          { id: 'hr-settings',              label: 'Settings',                 path: '/app/members/admin/settings',                icon: <IconSettings size={16} />,    visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN] },
          { id: 'hr-lookups',               label: 'Lookups',                  path: '/app/members/admin/lookups',                 icon: <IconDatabase size={16} />,    visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN] },
          { id: 'hr-payspace',              label: 'PaySpace Sync',            path: '/app/members/admin/payspace',                icon: <IconBuildingBank size={16} />, visible: hrAdmin, requiredRoles: [BACKEND_ROLES.HR_ADMIN] },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '2.0.0',
        lastUpdated: Date.now(),
        description: 'HR Management — staff self-service + HRAdmin operations',
      },
    };
  }
}

export const membersMenuProvider = new MembersMenuProvider();
